import React, { useContext, useState } from 'react'
import Confetti from 'react-confetti'
import { TabBar } from 'src/components/TabBar'
import { RowText } from 'src/components/RowText'

import { useConfetti } from 'src/hooks/useConfetti'
import { ArcContext } from 'src/contexts/ArcContext'
import { getSynthBySyntheticAddress } from 'src/hooks/useSynths'
import { quickWeiValuePow } from '@arcxmoney/arcx-core'
import { EarningSimulator } from 'src/components/Savings/EarningSimulator'
import { BigNumber, BigNumberish } from 'ethers'
import { parseEther } from 'ethers/lib/utils'
import Loader from 'react-loader-spinner'
import { Saving } from 'src/services/GraphService'
import { formatInputValue } from 'src/utils/format/formatInputValue'
import { getReadableNumber } from '../../helpers/getReadableNumber'
import { BASE } from '../../constants/crypto'
import './Savings.scss'
import { calculateEarnings } from './calculateEarning'
import { useErc20Balance } from 'src/hooks/useErc20Balance'

interface IProps {
  saving: Saving
}

enum TabMode {
  Deposit,
  Withdraw,
}

export function Savings(props: IProps) {
  const { arc, synths, getSyntheticBalance, isLoading, setIsLoading } = useContext(ArcContext)

  const [inputValue, setInputValue] = useState<BigNumber>(BigNumber.from(0))
  const [inputValueStr, setInputValueStr] = useState('0')
  const syntheticBalance = getSyntheticBalance(props.saving.syntheticAddress)
  const savingBalance = useErc20Balance(props.saving.id, arc?.signer)

  const { showConfetti, confetti } = useConfetti()

  const onInputValueChanged = (event) => {
    const formattedInputValue = formatInputValue(event.target.value)

    if (formattedInputValue) {
      setInputValue(formattedInputValue.inputValue)
      setInputValueStr(formattedInputValue.inputValueStr)
    }
  }

  const onMaxBtnClicked = (availableValue: BigNumber) => {
    setInputValue(availableValue)
    setInputValueStr(getReadableNumber(availableValue, 8))
  }

  const onDepositClicked = async (amount: BigNumberish) => {
    try {
      setIsLoading(true)
      const name = getSynthBySyntheticAddress(synths, props.saving.syntheticAddress).name
      const tx = await arc.stakeSavings(props.saving.id, amount, name)
      await tx.wait()
      showConfetti()
    } catch (err) {
      console.error('On deposit clicked', err)
    } finally {
      setIsLoading(false)
    }
  }

  const onWithdrawClicked = async (amount: BigNumberish) => {
    try {
      setIsLoading(true)
      const tx = await arc.withdrawSavings(props.saving.id, amount)
      await tx.wait()
    } catch (err) {
      console.error('On withdraw clicked', err)
    } finally {
      setIsLoading(false)
    }
  }

  const onUnstakeAllClicked = async () => {
    try {
      setIsLoading(true)
      const tx = await arc.unstakeAllSavings(props.saving.id)
      await tx.wait()
    } catch (err) {
      console.error('On Unstake all clicked', err)
    } finally {
      setIsLoading(false)
    }
  }

  function tabContents(
    mode: TabMode,
    availableValue: BigNumber,
    onPrimaryClick: (amount: BigNumberish) => void,
    onSecondaryClick?: () => Promise<void>,
  ) {
    return (
      <div className="savings__tab-contents">
        <RowText
          title="Available:"
          value={getReadableNumber(availableValue, 8) + ' STABLEx'}
          customTitleStyle={{
            color: '#D7DDE8',
            fontSize: '1rem',
            fontWeight: 500,
          }}
          customValueStyle={{
            color: '#757F9A',
            fontWeight: 700,
            fontSize: '1rem',
          }}
        />
        <div className="savings__text-input-container">
          <input
            className="savings__text-field"
            type="number"
            value={inputValueStr}
            onChange={onInputValueChanged}
          />
          <button className="savings__max-btn" onClick={() => onMaxBtnClicked(availableValue)}>
            MAX
          </button>
        </div>
        <div className="savings__buttons-container">
          <button
            type="button"
            className="savings__savings-cta-button"
            onClick={() => onPrimaryClick(inputValue)}
            disabled={isLoading || inputValue.gt(availableValue)}
          >
            {isLoading ? <Loader type="Grid" color="white" height={25} /> : TabMode[mode]}
          </button>
          {onSecondaryClick && (
            <button
              type="button"
              className="savings__savings-cta-button"
              onClick={() => onSecondaryClick()}
              disabled={isLoading || availableValue.lt(parseEther('0.00000001'))}
            >
              {isLoading ? <Loader type="Grid" color="white" height={25} /> : 'Unstake All'}
            </button>
          )}
        </div>
      </div>
    )
  }

  const apyRate = getReadableNumber(
    quickWeiValuePow(BASE.add(props.saving.savingsRate), BigNumber.from(364 * 24 * 60 * 60), 300)
      .sub(BASE)
      .mul(100),
    2,
  )

  return (
    <div className="savings">
      {confetti && <Confetti numberOfPieces={1000} recycle={false} />}
      <div className="savings__inner-page-container">
        <p className="savings__subtitle">Current Balance</p>
        <EarningSimulator saving={props.saving} savingBalance={savingBalance} />
        <p className="savings__subtitle">
          STABLEX @ {apyRate}
          {'% '}
          APY
        </p>
        <div className="savings__savings-card">
          <TabBar
            items={[
              {
                title: 'Deposit',
                component: tabContents(TabMode.Deposit, syntheticBalance, onDepositClicked),
              },
              {
                title: 'Withdraw',
                component: tabContents(
                  TabMode.Withdraw,
                  calculateEarnings(props.saving, savingBalance),
                  onWithdrawClicked,
                  onUnstakeAllClicked,
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}
